import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const emptyLine = () => ({ description: '', quantity: '1', unitPrice: '', itbisExento: false });

// Issues a debit note (E33): an extra charge on an invoice DGII already
// accepted. Creates it, then sends it straight to DGII; if sending fails the
// note stays in "Lista" and can be sent later from the Facturas list.
const DebitNoteModal = ({ invoice, onClose, onChanged }) => {
  const { user } = useAuth();
  const asking = user?.role !== 'admin'; // a sales user asks an admin instead of issuing the note
  const [reason, setReason] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [createdNote, setCreatedNote] = useState(null);

  const setLine = (i, patch) => setLines(prev => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const validLines = lines.filter(l => l.description.trim() && Number(l.quantity) > 0 && Number(l.unitPrice) > 0);

  const total = useMemo(() => validLines.reduce((sum, l) => {
    const line = Number(l.quantity) * Number(l.unitPrice);
    return sum + line + (l.itbisExento ? 0 : line * 0.18);
  }, 0), [validLines]);

  const submit = async () => {
    setBusy(true);
    setError('');
    let note = createdNote;
    try {
      if (!note) {
        const created = await API.post(`/invoices/${invoice._id}/debit-note`, {
          reason,
          ...(dueDate ? { dueDate } : {}),
          lines: validLines.map(l => ({
            description: l.description.trim(),
            quantity: Number(l.quantity),
            unitPrice: Number(l.unitPrice),
            itbisExento: l.itbisExento,
          })),
        });
        if (created.status === 202) { onChanged(); onClose(); return; } // asked of the admins (the toast says so)
        note = created.data;
        setCreatedNote(note);
      }
      const { data } = await API.post(`/invoices/${note._id}/send-ecf`);
      onChanged();
      if (data.dgiiOutcome === 'rejected') {
        setError(`DGII rechazó la nota de débito: ${data.invoice?.ecf?.dgiiMessage || 'sin detalle'}`);
        return;
      }
      onClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Error al emitir la nota de débito.';
      setError(note ? `La nota de débito ${note.number} se creó, pero no se pudo enviar a DGII: ${message}` : message);
      if (note) onChanged();
    } finally {
      setBusy(false);
    }
  };

  const retryable = createdNote && !error.startsWith('DGII rechazó');

  return (
    <Modal show onHide={busy ? undefined : onClose} size="lg" centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Nota de débito — {invoice.number}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          Modifica {invoice.ncf} · {invoice.client?.name} · Total {money(invoice.total)}. Cobra un monto adicional
          (aumento de precio, cargo por mora, servicio no facturado...).
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {asking && (
          <div className="alert alert-note small">
            Una nota necesita la aprobación de un administrador. La pides aquí y sigues con tu trabajo: cuando la apruebe, la nota se crea
            y se envía a DGII sola (verás el resultado en Aprobaciones).
          </div>
        )}


        <Form.Group className="mb-3">
          <Form.Label>Motivo <span className="text-muted small">(máx. 90 caracteres, va en el comprobante)</span></Form.Label>
          <Form.Control value={reason} maxLength={90} disabled={!!createdNote}
            onChange={e => setReason(e.target.value)} placeholder="Ej.: cargo por entrega fuera de zona" />
        </Form.Group>

        <Table size="sm" className="mb-2">
          <thead>
            <tr><th>Concepto (máx. 80)</th><th style={{ width: 90 }}>Cant.</th><th style={{ width: 130 }}>Precio</th><th style={{ width: 80 }}>Exento</th><th style={{ width: 40 }}></th></tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td><Form.Control size="sm" maxLength={80} disabled={!!createdNote} value={l.description}
                  onChange={e => setLine(i, { description: e.target.value })} /></td>
                <td><Form.Control size="sm" type="number" min="0" step="1" disabled={!!createdNote} value={l.quantity}
                  onChange={e => setLine(i, { quantity: e.target.value })} /></td>
                <td><Form.Control size="sm" type="number" min="0" step="0.01" disabled={!!createdNote} value={l.unitPrice}
                  onChange={e => setLine(i, { unitPrice: e.target.value })} /></td>
                <td className="text-center"><Form.Check disabled={!!createdNote} checked={l.itbisExento}
                  onChange={e => setLine(i, { itbisExento: e.target.checked })} /></td>
                <td>
                  {lines.length > 1 && !createdNote && (
                    <Button size="sm" variant="link" className="p-0 text-danger"
                      onClick={() => setLines(prev => prev.filter((_, idx) => idx !== i))}>×</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {!createdNote && (
          <Button size="sm" variant="outline-secondary" className="mb-3" onClick={() => setLines(prev => [...prev, emptyLine()])}>
            + Agregar concepto
          </Button>
        )}

        <Form.Group className="mb-2" style={{ maxWidth: 240 }}>
          <Form.Label>Vencimiento <span className="text-muted small">(opcional)</span></Form.Label>
          <Form.Control size="sm" type="date" value={dueDate} disabled={!!createdNote} onChange={e => setDueDate(e.target.value)} />
        </Form.Group>

        <div className="text-end">Total a cobrar (con ITBIS): <strong>{money(total)}</strong></div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={busy}>{createdNote ? 'Cerrar' : 'Cancelar'}</Button>
        {!(createdNote && !retryable) && (
          <Button onClick={submit} disabled={busy || !reason.trim() || (!createdNote && validLines.length === 0)}>
            {busy ? 'Enviando...' : retryable ? 'Reintentar envío a DGII' : asking ? 'Pedir aprobación' : 'Emitir y enviar a DGII'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DebitNoteModal;
